/**
 * @file DiscardDialog.tsx
 * @description Окно «Отменить изменения» из меню «⋯» (phase19.16 Р1;
 * uiux-design-r1 §0.1; контракт §5): выбор частей, которые сотрудник может
 * отменить (право правки части). Отмена не трогает сайт — черновик части
 * возвращается к опубликованному виду.
 */

import type { SiteViewPart } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useSiteView } from "../site-view-context";
import { useDiscard } from "../../../hooks/site-view/useDiscard";
import { partCountLabel } from "../../../lib/site-view/changes";

export function DiscardDialog() {
  const view = useSiteView();
  const { discard, busy } = useDiscard();
  const open = view.dialog === "discard";
  const parts = (view.changes?.changedParts ?? []).filter((p) => p.canDiscard);
  const [selected, setSelected] = useState<SiteViewPart[]>([]);

  // При открытии отмечены все части, которые можно отменить.
  useEffect(() => {
    if (open) setSelected(parts.map((p) => p.part));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только в момент открытия
  }, [open]);

  function toggle(part: SiteViewPart, on: boolean) {
    setSelected((prev) => (on ? [...prev, part] : prev.filter((p) => p !== part)));
  }

  async function onConfirm() {
    if (await discard(selected)) view.openDialog(null);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => view.openDialog(next ? "discard" : null)}>
      <DialogContent fullScreen>
        <DialogHeader>
          <DialogTitle>Отменить изменения</DialogTitle>
          <DialogDescription>
            Черновик выбранных частей вернётся к виду, который сейчас на сайте. Сайт не изменится.
          </DialogDescription>
        </DialogHeader>
        {parts.length === 0 ? (
          <p className="type-body text-on-surface-variant">Ваших изменений, которые можно отменить, нет.</p>
        ) : (
          <div className="flex flex-col">
            {parts.map((part) => (
              <label key={part.part} className="flex min-h-12 cursor-pointer items-center gap-3">
                <Checkbox
                  checked={selected.includes(part.part)}
                  onCheckedChange={(checked) => toggle(part.part, checked === true)}
                />
                <span className="type-body text-on-surface">
                  {partCountLabel(part)}
                  {part.editedBy ? <span className="text-on-surface-variant"> · {part.editedBy.name}</span> : null}
                </span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => view.openDialog(null)}>
            Не отменять
          </Button>
          <Button variant="destructive-solid" disabled={selected.length === 0} loading={busy} onClick={() => void onConfirm()}>
            Отменить изменения
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
